import { Button, Grid, Stack, TextField } from '@mui/material';
import { createCustomerPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { CustomFieldScalarValue } from '@vy/custom-fields-types';
import type {
  CustomerType,
  IntegrationProviders,
  NewCustomer,
  OverageSettings,
  UpdateCustomer,
} from '@vy/types';
import { CUSTOMER_TYPES, INTEGRATION_PROVIDERS, OVERAGE_SETTINGS } from '@vy/types';
import { isNotEmptyArray, isNotNullOrUndefined, raiseUnreachable } from '@vy/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { BaseSelect } from '../../../../../../components/design/selects/BaseSelect';
import { SubsidiarySelect } from '../../../../../../components/new-design/inputs/SubsidiarySelect';
import { useMorningEnabled } from '../../../../../../hooks/requests/integrations';
import { useActivatedIntegration } from '../../../../../../hooks/use-activated-integration';
import { useDialog } from '../../../../../../hooks/use-dialog';
import { vyFormRegister } from '../../../../../../utils/react-hook-forms-utils';
import { supportsLegacyErpId } from '../../../../../../utils/supports-legacy-erp-id';
import { MorningCustomersDropdown } from '../../../../customers-component/MorningCustomersDropdown';
import { NetSuiteCustomerInput } from '../../../../customers-component/NetSuiteCustomersInput';
import { QuickBooksCustomersDropdown } from '../../../../customers-component/QuickBooksCustomersDropdown/QuickBooksCustomersDropdown';
import type { CreateCustomerMode } from '../../../create-customer-page-types/CreateCustomerModeType';
import { ViewCrmCustomFieldsDialog } from '../../ViewCrmCustomFieldsDialog/ViewCrmCustomFieldsDialog';
import { MarketplaceDropdown } from '../../../../customers-component/MarketplaceDropdown';
import { MarketplaceIdTextInput } from '../../../../customers-component/MarketplaceIdTextInput';
import { XeroCustomersDropdown } from '../../../../customers-component/XeroCustomersDropdown';
import { CustomerIdentifierTextInput } from '../../../../customers-component/CustomerIdentifierTextInput';
import { VyFormSection } from '../../../../../../components/design/layout';
import { VyIcon } from '@vy/ui-design-system';
import { colors } from '../../../../../../assets/themes/colors.ts';

type CustomerConfigurationProps = CreateCustomerMode & {
  integrationProvider?: IntegrationProviders;
  crmCustomFieldsOptions?: { label: string; value: CustomFieldScalarValue | null }[];
};

export const CustomerConfiguration: React.FC<CustomerConfigurationProps> = ({
  mode = 'create',
  integrationProvider,
  crmCustomFieldsOptions,
}) => {
  const { isDialogOpen, handleClose, handleOpen } = useDialog();

  const { getValues, register, control, formState, setValue } =
    useFormContext<typeof mode extends 'create' ? NewCustomer : UpdateCustomer>();

  const legacyErpIdEnabled = supportsLegacyErpId();
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);
  const isNetSuiteInputVisible = isNetSuiteEnabled || legacyErpIdEnabled;

  const isQuickBooksEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.quickBooks);

  const isMorningEnabled = useMorningEnabled();

  const isMarketplaceEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.connact);

  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero);

  const isCustomFieldsVisible =
    isNotNullOrUndefined(integrationProvider) && isNotEmptyArray(crmCustomFieldsOptions);

  const onManualCrmIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const crmId = event.target.value;

    setValue('crmMetadata', { crmId }, { shouldDirty: true });
  };

  return (
    <VyFormSection
      title="Preferences"
      icon={<VyIcon icon="SettingEdit" height={18} width={18} viewBox="0 0 20 20" color={colors.grey[90]} />}
    >
      <Grid container rowSpacing={3} columnSpacing={2} size={12}>
        <Grid size={4}>
          <TextField
            {...getSelectorAttribute(createCustomerPageSelectors.configuration.externalId)}
            label="External ID"
            placeholder="e.g. 123456789"
            error={isNotNullOrUndefined(formState.errors.externalId)}
            helperText={formState.errors.externalId?.message}
            {...vyFormRegister(register, 'externalId')}
            fullWidth
          />
        </Grid>

        <Grid size={4}>
          <Stack
            sx={{
              alignItems: "flex-start",
              gap: 1
            }}>
            <TextField
              {...getSelectorAttribute(createCustomerPageSelectors.configuration.crmId)}
              label="CRM ID"
              placeholder="e.g. 123456789"
              {...register('crmMetadata.crmId')}
              onChange={onManualCrmIdChange}
              defaultValue={getValues('crmMetadata')?.crmId}
              fullWidth
              error={isNotNullOrUndefined(formState.errors.crmMetadata?.crmId)}
              helperText={formState.errors.crmMetadata?.crmId?.message}
            />
            {isCustomFieldsVisible && (
              <>
                <Button
                  variant="text"
                  disableRipple
                  onClick={handleOpen}
                  sx={{
                    color: '#2953FF',
                    padding: 0,
                    backgroundColor: 'transparent',
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                >
                  View custom fields
                </Button>
                <ViewCrmCustomFieldsDialog
                  open={isDialogOpen}
                  onClose={handleClose}
                  crmName={integrationProvider}
                  customFields={crmCustomFieldsOptions}
                />
              </>
            )}
          </Stack>
        </Grid>

        {isNetSuiteInputVisible && (
          <Grid size={4}>
            <NetSuiteCustomerInput showNetSuiteOptions={isNetSuiteEnabled} />
          </Grid>
        )}

        {isQuickBooksEnabled && (
          <Grid size={4}>
            <QuickBooksCustomersDropdown />
          </Grid>
        )}

        {isMorningEnabled && (
          <Grid size={4}>
            <MorningCustomersDropdown />
          </Grid>
        )}

        {isXeroEnabled && (
          <Grid size={4}>
            <XeroCustomersDropdown />
          </Grid>
        )}

        {isMarketplaceEnabled && (
          <>
            <Grid size={4}>
              <MarketplaceDropdown control={control} />
            </Grid>

            <Grid size={4}>
              <MarketplaceIdTextInput control={control} />
            </Grid>

            <Grid size={4}>
              <CustomerIdentifierTextInput control={control} />
            </Grid>
          </>
        )}

        <Grid size={4}>
          <Controller
            control={control}
            name="subsidiaryId"
            render={({ field }) => (
              <SubsidiarySelect primaryAsDefault label="Subsidiary" {...field} fullWidth />
            )}
          />
        </Grid>

        <Grid size={4}>
          <Controller
            control={control}
            name="settings.overage"
            render={({ field }) => (
              <BaseSelect
                {...getSelectorAttribute(createCustomerPageSelectors.configuration.overage)}
                label="Overage"
                values={Object.values(OVERAGE_SETTINGS)}
                defaultValue={getValues('settings.overage')}
                error={isNotNullOrUndefined(formState.errors.settings?.overage)}
                helperText={formState.errors.settings?.overage?.message}
                getLabelOption={(item) => getOverageSettingDescription(item as OverageSettings)}
                {...field}
                withNone={false}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid size={4}>
          <Controller
            control={control}
            name="settings.customerType"
            render={({ field }) => (
              <BaseSelect
                {...getSelectorAttribute(createCustomerPageSelectors.configuration.customerType)}
                label="Customer Type"
                values={Object.values(CUSTOMER_TYPES)}
                defaultValue={getValues('settings.customerType')}
                error={isNotNullOrUndefined(formState.errors.settings?.customerType)}
                helperText={formState.errors.settings?.customerType?.message}
                getLabelOption={(item) => item as CustomerType}
                {...field}
                withNone={false}
                fullWidth
              />
            )}
          />
        </Grid>
      </Grid>
    </VyFormSection>
  );
};

export function getOverageSettingDescription(setting: OverageSettings) {
  switch (setting) {
    case OVERAGE_SETTINGS.NoCharge:
      return 'Never charge';
    case OVERAGE_SETTINGS.ChargeTier:
      return 'Always charge';
    case OVERAGE_SETTINGS.ChargeExtras:
      return 'Charge separately at end date';
    default:
      return raiseUnreachable(setting);
  }
}
